/**
 * ClientStore - IT & Telco marketplace/web store
 * Product categories, search, cart, quote request
 */

import React, { useState, useMemo } from 'react';
import { clientFetch, getClientData, navigate } from './api';
import ClientShell from './ClientShell';

const PRODUCTS_BY_CATEGORY = {
  'Internet & NBN': [
    { id: 'nbn-basic', icon: '🌐', name: 'NBN Basic Plan', desc: 'Up to 12 Mbps', priceRange: '$79-89/mo' },
    { id: 'nbn-standard', icon: '🌐', name: 'NBN Standard Plan', desc: 'Up to 50 Mbps', priceRange: '$99-129/mo' },
    { id: 'nbn-premium', icon: '🌐', name: 'NBN Premium Plan', desc: 'Up to 100 Mbps', priceRange: '$149-179/mo' },
    { id: 'nbn-business', icon: '🌐', name: 'NBN Business Plan', desc: 'Up to 250 Mbps', priceRange: '$199-249/mo' },
    { id: 'backup-internet', icon: '📡', name: 'Backup Internet', desc: '4G LTE failover', priceRange: '$49-69/mo' },
  ],
  'Phone Systems': [
    { id: 'sip-trunking', icon: '☎️', name: 'SIP Trunking', desc: 'Per channel VoIP', priceRange: '$15-25/ch/mo' },
    { id: '3cx-phone', icon: '☎️', name: '3CX Phone System', desc: 'Cloud-based PBX', priceRange: '$30-50/user/mo' },
    { id: 'voip-licenses', icon: '☎️', name: 'VoIP User Licenses', desc: 'Per user VoIP', priceRange: '$12-18/user/mo' },
    { id: 'call-recording', icon: '🎙️', name: 'Call Recording', desc: 'Compliance & training', priceRange: '$20-40/mo' },
    { id: 'ivr-system', icon: '🤖', name: 'IVR/Call Routing', desc: 'Automated call distribution', priceRange: '$50-100/mo' },
  ],
  'Cybersecurity': [
    { id: 'cyber-audit', icon: '🔒', name: 'Cybersecurity Audit', desc: 'Full security assessment', priceRange: '$2,500-5,000' },
    { id: 'firewall', icon: '🛡️', name: 'Enterprise Firewall', desc: 'Next-gen network protection', priceRange: '$150-300/mo' },
    { id: 'mfa-licenses', icon: '🔐', name: 'Multi-Factor Auth', desc: 'MFA solution + support', priceRange: '$5-10/user/mo' },
    { id: 'email-security', icon: '✉️', name: 'Email Security', desc: 'Advanced threat protection', priceRange: '$8-15/user/mo' },
    { id: 'backup-dr', icon: '💾', name: 'Backup & Disaster Recovery', desc: 'Off-site data protection', priceRange: '$200-500/mo' },
    { id: 'security-training', icon: '👥', name: 'Security Awareness Training', desc: 'Staff training program', priceRange: '$30-60/user/yr' },
  ],
  'Cloud & IT': [
    { id: 'microsoft-365', icon: '☁️', name: 'Microsoft 365', desc: 'Business, Standard, Premium', priceRange: '$12.50-22/user/mo' },
    { id: 'azure-cloud', icon: '☁️', name: 'Microsoft Azure', desc: 'Cloud infrastructure', priceRange: 'Pay-as-you-go' },
    { id: 'cloud-storage', icon: '💾', name: 'Cloud Storage', desc: '1TB+ secure storage', priceRange: '$8-25/user/mo' },
    { id: 'video-conferencing', icon: '📹', name: 'Video Conferencing', desc: 'Teams, Webex, Zoom', priceRange: '$10-20/user/mo' },
    { id: 'email-hosting', icon: '✉️', name: 'Email Hosting', desc: 'Exchange Online hosting', priceRange: '$8-16/user/mo' },
  ],
  'Hardware': [
    { id: 'router', icon: '📶', name: 'Business WiFi Router', desc: 'Mesh WiFi 6 solution', priceRange: '$400-800' },
    { id: 'printer', icon: '🖨️', name: 'Managed Printer Fleet', desc: 'Xerox/HP with support', priceRange: '$100-300/mo' },
    { id: 'voip-phone', icon: '☎️', name: 'IP Phone System', desc: 'Cisco/Polycom handsets', priceRange: '$150-400 ea' },
    { id: 'network-switch', icon: '🔌', name: 'Network Switches', desc: 'Managed POE switches', priceRange: '$600-2,000' },
    { id: 'access-point', icon: '📡', name: 'Access Points', desc: 'Enterprise WiFi coverage', priceRange: '$200-400 ea' },
  ],
  'Managed Services': [
    { id: 'managed-it', icon: '🛠️', name: 'Managed IT Support', desc: '24/7 proactive monitoring', priceRange: '$800-2,000/mo' },
    { id: 'network-mgmt', icon: '🌐', name: 'Network Management', desc: 'Full network monitoring', priceRange: '$200-500/mo' },
    { id: 'help-desk', icon: '🎫', name: 'Help Desk Support', desc: 'Tier 1-3 support team', priceRange: '$500-1,500/mo' },
    { id: 'it-consulting', icon: '💼', name: 'IT Consulting', desc: 'Strategy & planning', priceRange: '$150-250/hr' },
    { id: 'compliance-mgmt', icon: '✅', name: 'Compliance Management', desc: 'ISO/SOC2/HIPAA ready', priceRange: '$300-800/mo' },
  ],
};

export default function ClientStore() {
  const clientData = getClientData();
  const tier = clientData?.tier || 'smb';
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [showQuoteForm, setShowQuoteForm] = useState(false);
  const [quoteForm, setQuoteForm] = useState({ name: '', email: '', phone: '', notes: '' });
  const [quoteSubmitting, setQuoteSubmitting] = useState(false);
  const [quoteSuccess, setQuoteSuccess] = useState(false);

  // Filter products by search
  const filteredCategories = useMemo(() => {
    const filtered = {};
    Object.keys(PRODUCTS_BY_CATEGORY).forEach((cat) => {
      const products = PRODUCTS_BY_CATEGORY[cat].filter((p) =>
        p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        p.desc.toLowerCase().includes(searchTerm.toLowerCase())
      );
      if (products.length > 0) {
        filtered[cat] = products;
      }
    });
    return filtered;
  }, [searchTerm]);

  const toggleProduct = (product) => {
    setSelectedProducts((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) {
        return prev.filter((p) => p.id !== product.id);
      }
      return [...prev, product];
    });
  };

  const handleQuoteSubmit = async () => {
    if (!quoteForm.name || !quoteForm.email) {
      alert('Please fill in Name and Email');
      return;
    }
    setQuoteSubmitting(true);
    try {
      const res = await clientFetch('/client/portal/quote', {
        method: 'POST',
        body: JSON.stringify({
          name: quoteForm.name,
          email: quoteForm.email,
          phone: quoteForm.phone,
          notes: quoteForm.notes,
          products: selectedProducts,
        }),
      });
      if (res.ok) {
        setQuoteSuccess(true);
        setSelectedProducts([]);
        setQuoteForm({ name: '', email: '', phone: '', notes: '' });
        setTimeout(() => {
          setShowQuoteForm(false);
          setQuoteSuccess(false);
        }, 2000);
      }
    } catch (err) {
      alert('Error submitting quote request');
    }
    setQuoteSubmitting(false);
  };

  const styles = {
    container: {
      maxWidth: '1400px',
    },
    searchBar: {
      display: 'flex',
      gap: '12px',
      marginBottom: '32px',
      alignItems: 'center',
    },
    searchInput: {
      flex: 1,
      padding: '12px 16px',
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      color: '#FFFFFF',
      fontSize: '14px',
      fontFamily: 'inherit',
    },
    searchBtn: {
      padding: '10px 20px',
      backgroundColor: '#0057FF',
      border: 'none',
      borderRadius: '6px',
      color: '#FFFFFF',
      cursor: 'pointer',
      fontSize: '14px',
      fontWeight: '600',
      transition: 'all 0.2s',
    },
    tierBadge: {
      display: 'inline-block',
      padding: '6px 12px',
      backgroundColor: tier === 'enterprise' ? '#C084FC' : '#00D4AA',
      color: '#0A0F1D',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '600',
      marginBottom: '16px',
    },
    sectionTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginTop: '40px',
      marginBottom: '20px',
    },
    categoryTitle: {
      fontSize: '18px',
      fontWeight: '600',
      color: '#FFFFFF',
      marginTop: '32px',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: '1px solid #2A3444',
    },
    productGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
      gap: '16px',
      marginBottom: '24px',
    },
    productCard: {
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '8px',
      padding: '20px',
      cursor: 'pointer',
      transition: 'all 0.3s',
      position: 'relative',
    },
    productCardHover: {
      backgroundColor: '#1A2332',
      borderColor: '#0057FF',
      boxShadow: '0 0 20px rgba(0, 87, 255, 0.15)',
    },
    productIcon: {
      fontSize: '32px',
      marginBottom: '12px',
    },
    productName: {
      fontSize: '16px',
      fontWeight: '600',
      color: '#FFFFFF',
      marginBottom: '8px',
    },
    productDesc: {
      fontSize: '13px',
      color: '#8B92A5',
      marginBottom: '12px',
    },
    productPrice: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#00D4AA',
      marginBottom: '16px',
    },
    productActions: {
      display: 'flex',
      gap: '8px',
    },
    actionBtn: {
      flex: 1,
      padding: '8px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    enquireBtn: {
      backgroundColor: 'transparent',
      border: '1px solid #0057FF',
      color: '#0057FF',
    },
    quoteBtn: {
      backgroundColor: '#0057FF',
      color: '#FFFFFF',
    },
    selectedCheckbox: {
      position: 'absolute',
      top: '12px',
      right: '12px',
      width: '20px',
      height: '20px',
      borderRadius: '4px',
      backgroundColor: '#0057FF',
      color: '#FFFFFF',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      fontSize: '12px',
      fontWeight: '700',
    },
    cartSummary: {
      position: 'fixed',
      bottom: '32px',
      right: '32px',
      backgroundColor: '#111827',
      border: '2px solid #0057FF',
      borderRadius: '12px',
      padding: '16px',
      minWidth: '280px',
      boxShadow: '0 8px 32px rgba(0, 87, 255, 0.15)',
    },
    cartTitle: {
      fontSize: '14px',
      fontWeight: '700',
      color: '#0057FF',
      marginBottom: '8px',
    },
    cartItems: {
      fontSize: '13px',
      color: '#BBBEC7',
      marginBottom: '12px',
      maxHeight: '120px',
      overflowY: 'auto',
    },
    cartItem: {
      padding: '4px 0',
      borderBottom: '1px solid #2A3444',
    },
    cartActions: {
      display: 'flex',
      gap: '8px',
      marginTop: '12px',
    },
    cartBtn: {
      flex: 1,
      padding: '8px 12px',
      border: 'none',
      borderRadius: '6px',
      fontSize: '12px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s',
    },
    quoteFormOverlay: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
    },
    quoteFormModal: {
      backgroundColor: '#111827',
      border: '1px solid #2A3444',
      borderRadius: '12px',
      padding: '32px',
      maxWidth: '600px',
      width: '90%',
      maxHeight: '80vh',
      overflowY: 'auto',
    },
    formTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: '#FFFFFF',
      marginBottom: '20px',
    },
    formGroup: {
      marginBottom: '16px',
    },
    formLabel: {
      display: 'block',
      fontSize: '13px',
      fontWeight: '600',
      color: '#BBBEC7',
      marginBottom: '6px',
    },
    formInput: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: '#0A0F1D',
      border: '1px solid #2A3444',
      borderRadius: '6px',
      color: '#FFFFFF',
      fontSize: '14px',
      fontFamily: 'inherit',
      boxSizing: 'border-box',
    },
    formTextarea: {
      width: '100%',
      padding: '10px 12px',
      backgroundColor: '#0A0F1D',
      border: '1px solid #2A3444',
      borderRadius: '6px',
      color: '#FFFFFF',
      fontSize: '14px',
      fontFamily: 'inherit',
      minHeight: '100px',
      resize: 'vertical',
      boxSizing: 'border-box',
    },
    formActions: {
      display: 'flex',
      gap: '12px',
      marginTop: '24px',
    },
    successMessage: {
      backgroundColor: '#00D4AA',
      color: '#0A0F1D',
      padding: '12px 16px',
      borderRadius: '6px',
      marginTop: '12px',
      fontSize: '13px',
      fontWeight: '600',
      textAlign: 'center',
    },
  };

  return (
    <ClientShell title="Product Store">
      <div style={styles.container}>
        {/* Tier Badge */}
        <div style={styles.tierBadge}>
          {tier === 'enterprise' ? '⭐ Enterprise Plan' : '📊 ' + (tier === 'midmarket' ? 'Mid-Market' : 'SMB')}
          {tier === 'enterprise' && ' + Dedicated Account Manager'}
        </div>

        {/* Search Bar */}
        <div style={styles.searchBar}>
          <input
            type="text"
            placeholder="Search products by name or description..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={styles.searchInput}
          />
          <button style={styles.searchBtn}>🔍 Search</button>
        </div>

        {/* Product Categories */}
        {Object.keys(filteredCategories).length > 0 ? (
          Object.entries(filteredCategories).map(([category, products]) => (
            <div key={category}>
              <div style={styles.categoryTitle}>{category}</div>
              <div style={styles.productGrid}>
                {products.map((product) => {
                  const isSelected = selectedProducts.some((p) => p.id === product.id);
                  return (
                    <div
                      key={product.id}
                      style={{
                        ...styles.productCard,
                        ...(isSelected ? styles.productCardHover : {}),
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = '#1A2332';
                        e.currentTarget.style.borderColor = '#0057FF';
                      }}
                      onMouseLeave={(e) => {
                        if (!isSelected) {
                          e.currentTarget.style.backgroundColor = '#111827';
                          e.currentTarget.style.borderColor = '#2A3444';
                        }
                      }}
                    >
                      {isSelected && <div style={styles.selectedCheckbox}>✓</div>}
                      <div style={styles.productIcon}>{product.icon}</div>
                      <div style={styles.productName}>{product.name}</div>
                      <div style={styles.productDesc}>{product.desc}</div>
                      <div style={styles.productPrice}>{product.priceRange}</div>
                      <div style={styles.productActions}>
                        <button
                          style={{ ...styles.actionBtn, ...styles.enquireBtn }}
                          onClick={() => navigate('/client/tickets')}
                        >
                          📧 Enquire
                        </button>
                        <button
                          style={{ ...styles.actionBtn, ...styles.quoteBtn }}
                          onClick={() => toggleProduct(product)}
                        >
                          {isSelected ? '✓ Added' : '➕ Add'}
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))
        ) : (
          <div style={{ textAlign: 'center', padding: '40px 20px', color: '#8B92A5' }}>
            <div style={{ fontSize: '32px', marginBottom: '12px' }}>🔍</div>
            <div>No products found matching "{searchTerm}"</div>
            <div style={{ fontSize: '13px', marginTop: '8px' }}>Try adjusting your search terms</div>
          </div>
        )}

        {/* Cart Summary */}
        {selectedProducts.length > 0 && (
          <div style={styles.cartSummary}>
            <div style={styles.cartTitle}>
              📦 Quote Cart ({selectedProducts.length} item{selectedProducts.length !== 1 ? 's' : ''})
            </div>
            <div style={styles.cartItems}>
              {selectedProducts.map((p) => (
                <div key={p.id} style={styles.cartItem}>
                  {p.icon} {p.name}
                </div>
              ))}
            </div>
            <div style={styles.cartActions}>
              <button
                style={{
                  ...styles.cartBtn,
                  backgroundColor: 'transparent',
                  border: '1px solid #0057FF',
                  color: '#0057FF',
                }}
                onClick={() => setSelectedProducts([])}
              >
                Clear
              </button>
              <button
                style={{ ...styles.cartBtn, backgroundColor: '#0057FF', color: '#FFFFFF' }}
                onClick={() => setShowQuoteForm(true)}
              >
                Request Quote
              </button>
            </div>
          </div>
        )}

        {/* Quote Request Form Modal */}
        {showQuoteForm && (
          <div style={styles.quoteFormOverlay} onClick={() => !quoteSubmitting && setShowQuoteForm(false)}>
            <div
              style={styles.quoteFormModal}
              onClick={(e) => e.stopPropagation()}
            >
              <div style={styles.formTitle}>Request a Quote</div>

              {quoteSuccess && (
                <div style={styles.successMessage}>
                  ✓ Quote request submitted successfully! Our team will contact you soon.
                </div>
              )}

              {!quoteSuccess && (
                <>
                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Contact Name *</label>
                    <input
                      type="text"
                      style={styles.formInput}
                      placeholder="Full name"
                      value={quoteForm.name}
                      onChange={(e) => setQuoteForm({ ...quoteForm, name: e.target.value })}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Email Address *</label>
                    <input
                      type="email"
                      style={styles.formInput}
                      placeholder="email@company.com"
                      value={quoteForm.email}
                      onChange={(e) => setQuoteForm({ ...quoteForm, email: e.target.value })}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Phone Number</label>
                    <input
                      type="tel"
                      style={styles.formInput}
                      placeholder="+61 2 XXXX XXXX"
                      value={quoteForm.phone}
                      onChange={(e) => setQuoteForm({ ...quoteForm, phone: e.target.value })}
                    />
                  </div>

                  <div style={styles.formGroup}>
                    <label style={styles.formLabel}>Additional Notes</label>
                    <textarea
                      style={styles.formTextarea}
                      placeholder="Any specific requirements or questions..."
                      value={quoteForm.notes}
                      onChange={(e) => setQuoteForm({ ...quoteForm, notes: e.target.value })}
                    />
                  </div>

                  <div style={{ fontSize: '13px', color: '#8B92A5', marginBottom: '16px' }}>
                    <strong>Selected Products:</strong>
                    {selectedProducts.map((p) => (
                      <div key={p.id} style={{ marginTop: '4px' }}>
                        {p.icon} {p.name} ({p.priceRange})
                      </div>
                    ))}
                  </div>

                  <div style={styles.formActions}>
                    <button
                      style={{
                        ...styles.cartBtn,
                        backgroundColor: 'transparent',
                        border: '1px solid #2A3444',
                        color: '#BBBEC7',
                      }}
                      onClick={() => setShowQuoteForm(false)}
                      disabled={quoteSubmitting}
                    >
                      Cancel
                    </button>
                    <button
                      style={{
                        ...styles.cartBtn,
                        backgroundColor: '#0057FF',
                        color: '#FFFFFF',
                        opacity: quoteSubmitting ? 0.6 : 1,
                      }}
                      onClick={handleQuoteSubmit}
                      disabled={quoteSubmitting}
                    >
                      {quoteSubmitting ? 'Submitting...' : 'Submit Quote Request'}
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}
      </div>
    </ClientShell>
  );
}
